import * as React from 'react';
import { useEffect } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import SendMoney from '../pages/SendMoney';
import { PrivateRoutes } from './middleWares/PrivateRoutes';
import { DATA_CHECK_TYPES, DEFAULT_VIEWS_BY_ROLE, REQUIRED_PROPERTIES, VIEWS_MAP } from './constants';
import LoginNavigation from '../flows/login/components/LoginNavigation';
import { Categorize } from '../flows/expenses/pages/Categorize';
import StatementsPage from '../flows/statements/pages';
import ProfitAndLoss from '../flows/profitAndLoss/pages';
import LiliCard from '../flows/liliCard/pages/LiliCard';
import EmergencyBucketStart from '../flows/emergencyBucketDashboard/pages/EmergencyBucketFirstSetUp';
import EmergencyBucketDashboard from '../flows/emergencyBucketDashboard/pages/EmergencyBucketDashboard';
import EmergencyBucketAddMoney from '../flows/emergencyBucketDashboard/pages/EmergencyBucketAdd';
import EmergencyBucketWithdrawMoney from '../flows/emergencyBucketDashboard/pages/EmergencyBucketWithdraw';
import EmergencyBucketTransactions from '../flows/EmergencyBucketTransactions';
import GoToApp from '../flows/goToApp';
import TaxBucketDashboard from '../flows/taxBucketDashboard/pages/TaxBucketDashboard';
import TaxBucketModifyFlow from '../flows/taxBucketDashboard/pages/TaxBucketModifyFlow';
import TaxBucketAdd from '../flows/taxBucketDashboard/pages/TaxBucketAdd';
import TaxBucketWithdraw from '../flows/taxBucketDashboard/pages/TaxBucketWithdraw';
import TaxBucketTransactions from '../flows/taxBucketTransactions';
import AppCenter from '../flows/appCenter/pages/AppCenter';
import Instructions from '../flows/appCenter/pages/instructions';
import { AddMoney } from '../flows/addMoney/pages';
import ScheduleC from '../flows/scheduleC/pages';
import Version from '../generic-components/Version';
import { trackMixPanel } from '../services/mixpanel.service';
import OrderNewCard from '../flows/liliCard/pages/OrderNewCard';
import CashDepositPage from '../flows/cashDeposit/pages/CashDeposit';
import BalanceUp from '../flows/balanceUp/pages';
import CardTracker from '../flows/cardTracker/pages';
import ActivateCard from '../flows/liliCard/pages/ActivateCard';
import ChangePin from '../flows/liliCard/pages/ChangePin';
import { Invoices } from '../pages/Invoices';
import { FEATURE_FLAG } from '../constants/general';
import { InvoiceCreation } from '../pages/Invoices/InvoiceCreation/InvoiceCreation';
import { MailCheck } from '../pages/MailCheck/pages/MailCheck';
import { PickSupplier } from '../pages/Supplier/pages/PickSupplier';
import { SavingsBucket } from '../pages/SavingsBucket/pages';
import { Limits } from '../pages/AccountLimits/pages/LimitsPage';
import { BillPay } from '../pages/BillPay/BillPay';
import { AddBill } from '../pages/BillPay/features/AddBill/pages';
import { UploadBill } from '../pages/BillPay/features/AddBill/pages/uploadBill';
import { CreateBill } from '../pages/BillPay/features/CreateBill/pages';
import { useStore } from '../store';
import { AchOutTransferRedesign } from '../pages/BankTransfer';
import { ManageSupplierV2 } from '../pages/Supplier/pages/ManageSupplierV2';
import PayBill from '../pages/BillPay/features/PayBill/pages/PayBill';
import AskYourVendor from '../pages/BillPay/features/PayBill/pages/AskYourVendor';
import AskYourVendorReview from '../pages/BillPay/features/PayBill/pages/AskYourVendorReview';
import { GlobalLoader } from '../components/GlobalLoader/GlobalLoader';
import { TaxForms } from '../pages/TaxForms/pages';
import { ForgotEmailFlow } from '../flows/login/pages/LoginHelp/ForgotEmail/ForgotEmailFlow';
import LoginHelp from '../flows/login/pages/LoginHelp/LoginHelp';
import { ForgotPasswordFlow } from '../flows/login/pages/LoginHelp/ForgotPassword/ForgotPasswordFlow';
import { TransactionsWrapper } from '../pages/TransactionsFacelift/components/TransactionsWrapper';
import { AccountantAi } from '../pages/AccountantAi/pages';
import { ServicesAndProductsHub } from '../pages/Invoices/ClientsAndProducts/ServicesAndProductsHub/ServicesAndProductHub';
import { ClientsHub } from '../pages/Invoices/ClientsAndProducts/ClientsHub/ClientsHub';
import { DashboardWrapper } from '../pages/DashboardFacelift';
import { GetStarted } from '../pages/AccountantOnboarding/pages/GetStarted';
import { PersonalDetails } from '../pages/AccountantOnboarding/pages/PersonalDetails';
import { VerifyPhone } from '../pages/AccountantOnboarding/pages/VerifyPhone';
import { SetPassword } from '../pages/AccountantOnboarding/pages/SetPassword';
import { CompleteOnboarding } from '../pages/AccountantOnboarding/pages/CompleteOnboarding';
import { OnboardingFailed } from '../pages/AccountantOnboarding/pages/OnboardingFailed';
import { InviteFormStep } from '../pages/InviteAccountant/pages/TeamMemberDetails/InviteFormStep';
import { VerifyAccountantPhone } from '../pages/InviteAccountant/pages/MFA/VerifyAccountantPhone';
import { VendorDetailsRequestFlow } from '../pages/VendorDetailsRequest/VendorDetailsRequestFlow';
import { MoveMoney } from '../pages/MoveMoney/pages/MoveMoney';
import { AccountSettings } from '../pages/AccountSettings/AccountSettings';
import { RoleSelectionStep } from '../pages/InviteAccountant/pages/RoleSelection/RoleSelectionStep';
import { RoutingNumberChange } from '../pages/RoutingNumberChange/RoutingNumberChange';
import { WireTransferNew } from '../pages/AddMoney/WireTransfer';
import { IntWireTransfer } from '../pages/AddMoney/IntWireTransfer';
import { AchTransfer } from '../pages/AddMoney/AchTransfer';
import { validateFeatureFlag } from '../services/featureFlagService';
import Expenses from '../flows/expenses/pages';
import { RoleType } from '../types';
import { FeatureAccess } from '../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';
import { LimitedFeatureType } from '../services/appAccess.service';

const { AUTH } = REQUIRED_PROPERTIES;
const {
  DEFAULT,
  LOGIN,
  TRANSACTIONS,
  EXPENSES,
  STATEMENTS,
  NOT_EXIST,
  DIRECT_DEPOSIT,
  CASH_DEPOSIT,
  CATEGORIZE,
  LILI_CARD,
  DASHBOARD,
  EMERGENCY_BUCKET_START,
  EMERGENCY_BUCKET_DASHBOARD,
  EMERGENCY_BUCKET_ADD_MONEY,
  EMERGENCY_BUCKET_WITHDRAW_MONEY,
  EMERGENCY_BUCKET_TRANSACTIONS,
  SEND_MONEY,
  INVOICES,
  TAX_BUCKET_DASHBOARD,
  TAX_BUCKET_PERSONALIZATION,
  TAX_BUCKET_ADD,
  TAX_BUCKET_WITHDRAW,
  TAX_BUCKET_TRANSACTIONS,
  CHECK_DEPOSIT,
  ADD_MONEY,
  APP_CENTER,
  APP_CENTER_INSTRUCTIONS,
  VERSION,
  ORDER_NEW_CARD,
  SCHEDULE_C_SET_UP,
  BALANCE_UP,
  ACH_OUT_TRANSFER,
  CARD_TRACKER,
  ACTIVATE_CARD,
  CHANGE_PIN,
  WIRE_TRANSFER,
  INTERNATIONAL_WIRE_TRANSFER,
  CREATE_INVOICE,
  SUPPLIER,
  MAIL_A_CHECK,
  SAVINGS_BUCKET,
  PROFIT_AND_LOSS,
  STATEMENT_OF_CASH_FLOWS,
  ACCOUNT_LIMITS,
  BILLS,
  RECOVERY,
  ACCOUNTANT_ONBOARDING,
  MOVE_MONEY,
  INVITE_ACCOUNTANT,
  ACCOUNT_SETTINGS,
  ROUTING_NUMBER_CHANGE,
} = VIEWS_MAP;
const DEFAULT_PAGE_BY_ROLE = {
  [RoleType.ACCOUNTANT]: TransactionsWrapper,
  [RoleType.ADMIN]: DashboardWrapper,
};
export const Router = () => {
  const location = useLocation();
  const { routerHistory, setRouterHistory } = useStore((state) => state);
  const { authorizedUserType } = useStore((state) => state.user);

  useEffect(() => {
    const { pathname } = location;
    const cleanPathName = pathname.replace('/', '');
    setRouterHistory([...routerHistory, cleanPathName]);
    trackMixPanel('Page Viewed', { pageName: cleanPathName });
  }, [location]);

  const defaultPageByRole =
    (authorizedUserType && DEFAULT_PAGE_BY_ROLE[authorizedUserType as keyof typeof DEFAULT_VIEWS_BY_ROLE]) ||
    DashboardWrapper;
  return useRoutes([
    {
      path: DEFAULT,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: defaultPageByRole,
      }),
    },
    {
      path: `${SUPPLIER.BASE}/${SUPPLIER.MANAGE.BASE}/${SUPPLIER.MANAGE.PARAM}`,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ManageSupplierV2,
        limitedFeature: LimitedFeatureType.BillPay,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: `${SUPPLIER.BASE}/${SUPPLIER.PICKER}`,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: PickSupplier,
        limitedFeature: LimitedFeatureType.BillPay,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: ROUTING_NUMBER_CHANGE,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: RoutingNumberChange,
        featureFlagName: FEATURE_FLAG.PAYEES_MIGRATION_REPORT,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.BusinessDetails,
      }),
    },
    {
      path: MAIL_A_CHECK,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: MailCheck,
        limitedFeature: LimitedFeatureType.SendMoneyMailACheck,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: ACCOUNT_SETTINGS,
      element: PrivateRoutes({ requiredProperty: AUTH, Next: AccountSettings }),
    },
    {
      path: LOGIN,
      element: <LoginNavigation />,
    },
    {
      path: ACCOUNT_LIMITS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: Limits,
        limitedFeature: LimitedFeatureType.InAppLimits,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: ADD_MONEY,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: AddMoney,
        limitedFeature: LimitedFeatureType.AddMoneyACHLinkedAccount,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: SEND_MONEY,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: SendMoney,
        limitedFeature: LimitedFeatureType.SendMoneyACHLinkedAccount,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: DASHBOARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: DashboardWrapper,
        limitedFeature: LimitedFeatureType.DashboardWidgetsAndAccountBalance,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: TRANSACTIONS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TransactionsWrapper,
        limitedFeature: LimitedFeatureType.TransactionsListView,
        minAccess: FeatureAccess.READ_ONLY,
      }),
    },
    {
      path: CATEGORIZE,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: Categorize,
        limitedFeature: LimitedFeatureType.TransactionListCategorize,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: DIRECT_DEPOSIT,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: AchTransfer,
        limitedFeature: LimitedFeatureType.AddMoneyACHDirectDeposit,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: WIRE_TRANSFER,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: WireTransferNew,
        limitedFeature: LimitedFeatureType.AddMoneyIncomingWireTransfer,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: INTERNATIONAL_WIRE_TRANSFER,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: IntWireTransfer,
        limitedFeature: LimitedFeatureType.SendMoneyWireOut,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: CASH_DEPOSIT,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: CashDepositPage,
        limitedFeature: LimitedFeatureType.AddMoneyCashDeposit,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: STATEMENTS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: StatementsPage,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Reports,
      }),
    },
    {
      path: PROFIT_AND_LOSS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ProfitAndLoss,
        limitedFeature: LimitedFeatureType.Reports,
        minAccess: FeatureAccess.READ_ONLY,
      }),
    },
    {
      path: STATEMENT_OF_CASH_FLOWS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ProfitAndLoss,
        limitedFeature: LimitedFeatureType.Reports,
        minAccess: FeatureAccess.READ_ONLY,
      }),
    },
    {
      path: EXPENSES,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: validateFeatureFlag(FEATURE_FLAG.EXPENSE_REPORT_V2) ? ProfitAndLoss : Expenses,
        limitedFeature: LimitedFeatureType.Reports,
        minAccess: FeatureAccess.READ_ONLY,
      }),
    },
    {
      path: LILI_CARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: LiliCard,
        limitedFeature: LimitedFeatureType.CardActions,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: NOT_EXIST,
      element: <LoginNavigation />,
    },
    {
      path: VERSION,
      element: <Version ver={process.env.REACT_APP_VERSION} />,
    },
    {
      path: EMERGENCY_BUCKET_START,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: EmergencyBucketStart,
        limitedFeature: LimitedFeatureType.TaxBucket,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: EMERGENCY_BUCKET_DASHBOARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: EmergencyBucketDashboard,
        validateData: DATA_CHECK_TYPES.EMERGENCY_BUCKET_DASHBOARD,
        fallbackRoute: `/${VIEWS_MAP.DASHBOARD}`,
        limitedFeature: LimitedFeatureType.TaxBucket,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: EMERGENCY_BUCKET_ADD_MONEY,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: EmergencyBucketAddMoney,
        limitedFeature: LimitedFeatureType.TaxBucket,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: EMERGENCY_BUCKET_WITHDRAW_MONEY,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: EmergencyBucketWithdrawMoney,
        limitedFeature: LimitedFeatureType.TaxBucket,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: EMERGENCY_BUCKET_TRANSACTIONS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: EmergencyBucketTransactions,
        limitedFeature: LimitedFeatureType.TaxBucket,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: BILLS.BASE,
      children: [
        {
          path: '',
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: BillPay,
            minAccess: FeatureAccess.READ_ONLY,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
        {
          path: BILLS.ADD_NEW_BILL,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: AddBill,
            featureFlagName: FEATURE_FLAG.BILL_PAY,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
        {
          path: BILLS.UPLOAD_BILL_FILE,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: UploadBill,
            featureFlagName: FEATURE_FLAG.BILL_PAY,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
        {
          path: `${BILLS.CREATE_BILL.BASE}${BILLS.CREATE_BILL.PARAM}`,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: CreateBill,
            featureFlagName: FEATURE_FLAG.BILL_PAY,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
        {
          path: `${BILLS.PAY_BILL.BASE}${BILLS.PAY_BILL.PARAM}`,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: PayBill,
            featureFlagName: FEATURE_FLAG.BILL_PAY,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.SendMoneyPayBill,
          }),
        },
        {
          path: `${BILLS.ASK_VENDOR.BASE}`,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: AskYourVendor,
            featureFlagName: FEATURE_FLAG.ASK_VENDOR_FOR_DETAILS,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
        {
          path: `${BILLS.ASK_VENDOR.BASE}${BILLS.ASK_VENDOR.REVIEW}`,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: AskYourVendorReview,
            featureFlagName: FEATURE_FLAG.ASK_VENDOR_FOR_DETAILS,
            minAccess: FeatureAccess.READ_WRITE,
            limitedFeature: LimitedFeatureType.BillPay,
          }),
        },
      ],
    },
    {
      path: INVOICES,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: Invoices,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Invoices,
      }),
    },
    {
      path: TAX_BUCKET_DASHBOARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxBucketDashboard,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.TaxBucket,
      }),
    },
    {
      path: TAX_BUCKET_PERSONALIZATION,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxBucketModifyFlow,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.TaxBucket,
      }),
    },
    {
      path: TAX_BUCKET_ADD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxBucketAdd,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.TaxBucket,
      }),
    },
    {
      path: TAX_BUCKET_WITHDRAW,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxBucketWithdraw,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.TaxBucket,
      }),
    },
    {
      path: TAX_BUCKET_TRANSACTIONS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxBucketTransactions,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.TaxBucket,
      }),
    },
    {
      path: APP_CENTER,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: AppCenter,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.AppCenter,
      }),
    },
    {
      path: APP_CENTER_INSTRUCTIONS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: Instructions,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.AppCenter,
      }),
    },
    {
      path: CHECK_DEPOSIT,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: GoToApp,
        limitedFeature: LimitedFeatureType.AddMoneyCheckDeposit,
        minAccess: FeatureAccess.READ_WRITE,
      }),
    },
    {
      path: ORDER_NEW_CARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: OrderNewCard,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.CardActions,
      }),
    },
    {
      path: CARD_TRACKER,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: CardTracker,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.CardActions,
      }),
    },
    {
      path: SCHEDULE_C_SET_UP,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ScheduleC,
        fallbackRoute: `/${VIEWS_MAP.DASHBOARD}`,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Reports,
      }),
    },
    {
      path: BALANCE_UP,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: BalanceUp,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.BalanceToggle,
      }),
    },
    {
      path: ACH_OUT_TRANSFER,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: AchOutTransferRedesign,
        featureFlagName: FEATURE_FLAG.NEW_ACH_OUT,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.SendMoneyACH,
      }),
    },
    {
      path: ACTIVATE_CARD,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ActivateCard,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.CardActions,
      }),
    },
    {
      path: CHANGE_PIN,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ChangePin,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.CardActions,
      }),
    },
    {
      path: CREATE_INVOICE,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: InvoiceCreation,
        featureFlagName: FEATURE_FLAG.INVOICE,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.Invoices,
      }),
    },
    {
      path: `${SAVINGS_BUCKET.BASE}`,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: SavingsBucket,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.SavingsSubAccount,
      }),
    },
    {
      path: VIEWS_MAP.TAX_FORM,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TaxForms,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Reports,
      }),
    },
    {
      path: RECOVERY,
      children: [
        {
          path: '',
          element: <LoginHelp />,
        },
        {
          path: 'password',
          element: <ForgotPasswordFlow />,
        },
        {
          path: 'email',
          element: <ForgotEmailFlow />,
        },
        {
          path: 'phone',
          element: <div>RESET phone</div>,
        },
      ],
    },
    {
      path: VIEWS_MAP.ACCOUNTANT_AI,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: AccountantAi,
        featureFlagName: FEATURE_FLAG.ACCOUNTANT_AI,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.AccountantAI,
      }),
    },
    {
      path: VIEWS_MAP.SERVICES_AND_PRODUCTS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ServicesAndProductsHub,
        featureFlagName: FEATURE_FLAG.INVOICE,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Invoices,
      }),
    },
    {
      path: VIEWS_MAP.CLIENTS,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: ClientsHub,
        featureFlagName: FEATURE_FLAG.INVOICE,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Invoices,
      }),
    },
    {
      path: 'all-invoices',
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: Invoices,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.Invoices,
      }),
    },
    {
      path: 'tfl',
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: TransactionsWrapper,
        minAccess: FeatureAccess.READ_ONLY,
        limitedFeature: LimitedFeatureType.TransactionsListView,
      }),
    },
    {
      path: ACCOUNTANT_ONBOARDING.START,
      children: [
        {
          path: '',
          element: <GetStarted />,
        },
        {
          path: ACCOUNTANT_ONBOARDING.PERSONAL_DETAILS,
          element: <PersonalDetails />,
        },
        {
          path: ACCOUNTANT_ONBOARDING.VERIFY_PHONE,
          element: <VerifyPhone />,
        },
        {
          path: ACCOUNTANT_ONBOARDING.CREATE_PASSWORD,
          element: <SetPassword />,
        },
        {
          path: ACCOUNTANT_ONBOARDING.COMPLETE_OB,
          element: <CompleteOnboarding />,
        },
        {
          path: ACCOUNTANT_ONBOARDING.OB_FAILED.BASE + ACCOUNTANT_ONBOARDING.OB_FAILED.PARAM,
          element: <OnboardingFailed />,
        },
        {
          path: '*',
          element: <GetStarted />,
        },
      ],
    },
    {
      path: INVITE_ACCOUNTANT.INVITE,
      children: [
        {
          path: '',
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: RoleSelectionStep,
            limitedFeature: LimitedFeatureType.TeamMemberPermission,
            minAccess: FeatureAccess.READ_WRITE,
          }),
        },
        {
          path: INVITE_ACCOUNTANT.DETAILS,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: InviteFormStep,
            limitedFeature: LimitedFeatureType.TeamMemberPermission,
            minAccess: FeatureAccess.READ_WRITE,
          }),
        },
        {
          path: INVITE_ACCOUNTANT.VERIFY_PHONE,
          element: PrivateRoutes({
            requiredProperty: AUTH,
            Next: VerifyAccountantPhone,
            limitedFeature: LimitedFeatureType.TeamMemberPermission,
            minAccess: FeatureAccess.READ_WRITE,
          }),
        },
      ],
    },
    {
      path: MOVE_MONEY.BASE + MOVE_MONEY.PARAM,
      element: PrivateRoutes({
        requiredProperty: AUTH,
        Next: MoveMoney,
        minAccess: FeatureAccess.READ_WRITE,
        limitedFeature: LimitedFeatureType.SendMoneyACH,
      }),
    },

    {
      path: VIEWS_MAP.GLOBAL_LOADER,
      element: <GlobalLoader />,
    },
    // TODO: ****** Make this route accessible for non logged in users *****
    {
      path: VIEWS_MAP.VENDOR_DETAILS_REQUEST,
      element: <VendorDetailsRequestFlow />,
    },
  ]);
};
